
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  checkSuspiciousRentals, 
  analyzeTemporalAnomalies, 
  checkStatusInconsistencies,
  checkIoTDiagnostics,
  checkFinancialDiagnostics,
  checkDataConsistency,
  checkBusinessLogic,
  checkSystemHealth
} from './DiagnosticsChecks';
import { DiagnosticsStats, InconsistencyData, DiagnosticsContextType, FilterState, DIAGNOSTICS_START_DATE } from './DiagnosticsTypes';

const DiagnosticsContext = createContext<DiagnosticsContextType | undefined>(undefined);

export const DiagnosticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stats, setStats] = useState<DiagnosticsStats>({
    totalBikes: 0,
    activeBikes: 0,
    inactiveBikes: 0,
    blockedBikes: 0,
    lowBatteryBikes: 0,
    totalRentals: 0,
    activeRentals: 0,
    suspiciousRentals: 0,
    totalRevenue: 0,
    iotStats: {
      totalDevices: 0,
      onlineDevices: 0,
      offlineDevices: 0,
      batteryIssues: 0,
      connectionIssues: 0,
      lastUpdateIssues: 0,
      performanceIssues: 0,
      totalBikes: 0,
      withIoT: 0,
      coveragePercent: 0,
      activePercent: 0,
      gpsDataQuality: 0
    },
    suspiciousRentalStats: {
      totalSuspicious: 0,
      byTariffType: { weekly: 0, biweekly: 0, monthly: 0, yandex: 0, unknown: 0 },
      potentialLoss: 0,
      coverageRate: 0,
      detectionAccuracy: 95,
      criticalUnderpayments: 0,
      temporalViolations: 0,
      earlyPayments: 0,
      latePayments: 0,
      rapidPayments: 0
    },
    financialStats: {
      totalPayments: 0,
      successfulPayments: 0,
      failedPayments: 0,
      pendingPayments: 0,
      missingReceipts: 0,
      amountDiscrepancies: 0,
      totalRevenue: 0,
      expectedRevenue: 0,
      revenueLoss: 0,
      successRate: 0,
      receiptCoverage: 0,
      canceledAuthorizations: 0,
      authorizationIssues: 0
    }
  });

  const [inconsistencies, setInconsistencies] = useState<InconsistencyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    selectedSeverity: [],
    selectedType: [],
    searchTerm: '',
    showOnlyIssues: false,
    useCustomDateRange: false,
    dateFrom: new Date(DIAGNOSTICS_START_DATE),
    dateTo: new Date(),
    selectedRentalStatus: [],
    ignoreOldRentals: false,
    ignoreCancelledRentals: false
  });

  const fetchDiagnosticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Определяем диапазон дат для фильтрации
      const dateFrom = filters.useCustomDateRange ? filters.dateFrom : new Date(DIAGNOSTICS_START_DATE);
      const dateTo = filters.useCustomDateRange ? filters.dateTo : new Date();
      
      const dateFromStr = dateFrom.toISOString();
      const dateToStr = dateTo.toISOString();

      console.log(`Загрузка данных диагностики с ${dateFromStr} по ${dateToStr}`);

      // Получаем данные с фильтрацией по датам
      const [bikesResult, rentalsFilteredResult, allRentalsResult, couriersResult, paymentsResult, trackingResult] = await Promise.all([
        supabase.from('bikes').select('*'),
        // Отфильтрованные аренды для анализа (по выбранному периоду)
        supabase.from('rentals').select('*').gte('start_date', dateFromStr).lte('start_date', dateToStr),
        // ВСЕ аренды для проверки статусов велосипедов (независимо от периода)
        supabase.from('rentals').select('*'),
        supabase.from('couriers').select('*'),
        supabase.from('payments').select('*').gte('payment_date', dateFromStr).lte('payment_date', dateToStr),
        supabase.from('bike_tracking_history')
          .select('bike_id, created_at')
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
      ]);

      if (bikesResult.error) throw bikesResult.error;
      if (rentalsFilteredResult.error) throw rentalsFilteredResult.error;
      if (allRentalsResult.error) throw allRentalsResult.error;
      if (couriersResult.error) throw couriersResult.error;
      if (paymentsResult.error) throw paymentsResult.error;
      if (trackingResult.error) throw trackingResult.error;

      const bikes = bikesResult.data || [];
      const rentalsFiltered = rentalsFilteredResult.data || [];
      const allRentals = allRentalsResult.data || [];
      const couriers = couriersResult.data || [];
      const payments = paymentsResult.data || [];
      const trackingHistory = trackingResult.data || [];

      console.log(`Загружено данных: велосипеды=${bikes.length}, отфильтрованные аренды=${rentalsFiltered.length}, все аренды=${allRentals.length}, платежи=${payments.length}`);

      // Запускаем все проверки с правильными данными
      const suspiciousRentalsResult = checkSuspiciousRentals(rentalsFiltered, couriers);
      const temporalAnomalies = analyzeTemporalAnomalies(rentalsFiltered, payments);
      // Для проверки статусов используем ВСЕ аренды, а не только отфильтрованные
      const statusInconsistencies = checkStatusInconsistencies(bikes, allRentals);
      const iotResult = checkIoTDiagnostics(bikes, trackingHistory);
      const financialResult = checkFinancialDiagnostics(payments, rentalsFiltered);
      const dataConsistency = checkDataConsistency(bikes, rentalsFiltered, couriers);
      const businessLogic = checkBusinessLogic(bikes, rentalsFiltered);
      const systemHealth = checkSystemHealth(bikes, rentalsFiltered, couriers, payments);

      // Собираем все несоответствия и обогащаем их информацией о статусе аренды
      let allInconsistencies = [
        ...suspiciousRentalsResult.inconsistencies,
        ...temporalAnomalies,
        ...statusInconsistencies,
        ...iotResult.inconsistencies,
        ...financialResult.inconsistencies,
        ...dataConsistency,
        ...businessLogic,
        ...systemHealth
      ];

      // Обогащаем данные информацией о статусе и периоде аренды
      allInconsistencies = allInconsistencies.map(inconsistency => {
        if (inconsistency.rental) {
          const rental = inconsistency.rental;
          return {
            ...inconsistency,
            rentalStatus: rental.status,
            rentalPeriodDisplay: rental.start_date ? 
              `${new Date(rental.start_date).toLocaleDateString('ru-RU')} - ${rental.end_date ? new Date(rental.end_date).toLocaleDateString('ru-RU') : 'Активная'}` : 
              'Не указан'
          };
        }
        return inconsistency;
      });

      // Применяем дополнительные фильтры
      allInconsistencies = applyAdditionalFilters(allInconsistencies, dateFrom);

      // Вычисляем общую статистику
      const newStats: DiagnosticsStats = {
        totalBikes: bikes.length,
        activeBikes: bikes.filter(b => b.status === 'active').length,
        inactiveBikes: bikes.filter(b => b.status === 'inactive').length,
        blockedBikes: bikes.filter(b => b.status === 'blocked').length,
        lowBatteryBikes: bikes.filter(b => b.battery_percentage < 20).length,
        totalRentals: rentalsFiltered.length,
        activeRentals: rentalsFiltered.filter(r => r.status === 'active').length,
        suspiciousRentals: suspiciousRentalsResult.stats.totalSuspicious,
        totalRevenue: financialResult.stats.totalRevenue,
        iotStats: iotResult.stats,
        suspiciousRentalStats: suspiciousRentalsResult.stats,
        financialStats: financialResult.stats
      };

      setStats(newStats);
      setInconsistencies(allInconsistencies);
    } catch (err) {
      console.error('Ошибка при загрузке данных диагностики:', err);
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  // Функция применения дополнительных фильтров
  const applyAdditionalFilters = (inconsistencies: InconsistencyData[], dateFrom: Date) => {
    return inconsistencies.filter(inconsistency => {
      // Фильтр по статусу аренды
      if (filters.selectedRentalStatus.length > 0 && inconsistency.rentalStatus) {
        if (!filters.selectedRentalStatus.includes(inconsistency.rentalStatus)) {
          return false;
        }
      }

      // Игнорировать отмененные аренды
      if (filters.ignoreCancelledRentals && inconsistency.rentalStatus === 'cancelled') {
        return false;
      }

      // Игнорировать старые аренды (начавшиеся до выбранного периода)
      if (filters.ignoreOldRentals && inconsistency.rental?.start_date) {
        const rentalStartDate = new Date(inconsistency.rental.start_date);
        if (rentalStartDate < dateFrom) {
          return false;
        }
      }

      return true;
    });
  };

  useEffect(() => {
    fetchDiagnosticsData();
  }, [
    filters.useCustomDateRange, 
    filters.dateFrom, 
    filters.dateTo,
    filters.selectedRentalStatus,
    filters.ignoreOldRentals,
    filters.ignoreCancelledRentals
  ]);

  const refresh = async () => {
    await fetchDiagnosticsData();
  };

  const updateFilters = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const exportData = () => {
    const exportInfo = {
      dateRange: {
        useCustomDateRange: filters.useCustomDateRange,
        dateFrom: filters.dateFrom.toISOString(),
        dateTo: filters.dateTo.toISOString()
      },
      stats,
      inconsistencies,
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(exportInfo, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `diagnostics_${filters.dateFrom.toISOString().split('T')[0]}_${filters.dateTo.toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const contextValue: DiagnosticsContextType = {
    stats,
    inconsistencies,
    loading,
    error,
    refresh,
    // Добавляем недостающие свойства для совместимости
    data: inconsistencies,
    filters,
    setFilters: updateFilters,
    refreshData: refresh,
    exportData,
    isLoading: loading
  };

  return (
    <DiagnosticsContext.Provider value={contextValue}>
      {children}
    </DiagnosticsContext.Provider>
  );
};

export const useDiagnostics = (): DiagnosticsContextType => {
  const context = useContext(DiagnosticsContext);
  if (context === undefined) {
    throw new Error('useDiagnostics must be used within a DiagnosticsProvider');
  }
  return context;
};
